import { z } from 'zod';

/**
 * Single source of truth for environment configuration.
 *
 * - Validates at module load via zod.
 * - Fails loudly when a required-at-boot var is missing (no silent absence).
 * - Auth provider vars are optional until the concrete provider is wired
 *   (see @shared/auth/auth-provider.tsx).
 */
const envSchema = z.object({
  VITE_BACKEND_URL: z.string().optional().default(''),

  /**
   * Public host of the ArmorIQ LLM gateway. Shown to customers as the base URL
   * they paste into their agent; never called from the browser. Left unset it
   * follows VITE_APP_ENV, so a staging build cannot hand out the production
   * host: that host rejects a staging org token with 403 and the page just
   * waits forever for a trace that will never arrive.
   */
  // '' is coerced to absent on purpose. A build arg that resolves to an empty
  // string would otherwise fail url() and fail the WHOLE schema, which resets
  // every other var -- including VITE_APP_ENV -- to its default.
  VITE_GATEWAY_URL: z
    .string()
    .transform((value) => value.trim())
    .refine((value) => value === '' || z.string().url().safeParse(value).success, {
      message: 'must be a URL, or empty to follow VITE_APP_ENV',
    })
    .transform((value) => (value === '' ? undefined : value))
    .optional(),

  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),

  /**
   * Annotation-collector endpoint for the dev-only Agentation toolbar. Empty by
   * default so the toolbar is OFF unless a developer explicitly points it at a
   * running collector — otherwise it spams `ERR_CONNECTION_REFUSED` on every
   * page load when nothing is listening on the port.
   */
  VITE_AGENTATION_ENDPOINT: z.string().optional().default(''),

  VITE_POSTHOG_KEY: z.string().optional().default(''),
  VITE_POSTHOG_HOST: z.string().optional().default('https://us.i.posthog.com'),

  VITE_FIREBASE_API_KEY: z.string().optional().default(''),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().optional().default(''),
  VITE_FIREBASE_PROJECT_ID: z.string().optional().default(''),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().optional().default(''),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().optional().default(''),
  VITE_FIREBASE_APP_ID: z.string().optional().default(''),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((issue) => `  • ${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('\n');

  console.warn(
    `\n[env] Invalid or missing environment variables:\n${issues}\n` +
      `\nCheck .env.local against .env.example. See docs/prototype-context/03_V1_ARCHITECTURE.md §11.\n`
  );
}

const data = parsed.success ? parsed.data : envSchema.parse({});

const GATEWAY_HOSTS = {
  production: 'https://gw.armoriq.ai',
  staging: 'https://staging-gw.armoriq.ai',
  development: 'https://staging-gw.armoriq.ai',
} as const;

export const env = Object.freeze({
  BACKEND_URL: data.VITE_BACKEND_URL,
  GATEWAY_URL: data.VITE_GATEWAY_URL ?? GATEWAY_HOSTS[data.VITE_APP_ENV],
  APP_ENV: data.VITE_APP_ENV,
  IS_DEV: data.VITE_APP_ENV === 'development',
  AGENTATION_ENDPOINT: data.VITE_AGENTATION_ENDPOINT,
  POSTHOG_KEY: data.VITE_POSTHOG_KEY,
  POSTHOG_HOST: data.VITE_POSTHOG_HOST,
  IS_PROD: data.VITE_APP_ENV === 'production',
  IS_STAGING: data.VITE_APP_ENV === 'staging',
  firebase: {
    apiKey: data.VITE_FIREBASE_API_KEY,
    authDomain: data.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: data.VITE_FIREBASE_PROJECT_ID,
    storageBucket: data.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: data.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: data.VITE_FIREBASE_APP_ID,
  },
} as const);

export type Env = typeof env;
